import { useMemo, useState, useEffect, useCallback } from "react";
import { PLATFORM_LOGOS, UserConnection } from "@/types/connection";
import { Platform, ConnectionType } from "@/types/enums";
import { Field } from "@/types/field";
import {
    ButtonGroup,
    Button,
    Card,
    CardBody,
    Skeleton,
    Input,
    CheckboxGroup,
    Checkbox,
    Chip,
    Switch,
} from "@heroui/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUpRightFromSquare,
    faArrowLeft,
    faSearch,
    faLink,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";

function NewConnection({
    growerId,
    fieldId,
    rxId,
    platformFilter = [],
    onFinish,
}: {
    growerId?: number | string;
    fieldId?: number;
    rxId?: number;
    platformFilter?: Platform[];
    onFinish?: (
        connectionType: ConnectionType,
        connectionId: number,
        externalId?: string,
    ) => void;
}) {
    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const getConnections = useGetRequest<UserConnection[]>({
        url: "user_connections/",
        method: "get",
        errorMessage: t("error.failed_to_fetch_connections"),
    });

    const [connectionSearchText, setConnectionSearchText] = useState("");

    const accountOptions = useMemo(() => {
        if (!getConnections.data) return [];
        return getConnections.data.filter((connection) => {
            if (
                platformFilter.length &&
                !platformFilter.includes(connection.platform)
            ) {
                return false;
            }
            if (!connectionSearchText) return true;
            const lowerSearchText = connectionSearchText.toLowerCase();
            return (
                connection?.username
                    ?.toLowerCase()
                    ?.includes(lowerSearchText) ||
                connection?.platform?.toLowerCase()?.includes(lowerSearchText)
            );
        });
    }, [getConnections.data, connectionSearchText, platformFilter]);

    const [selectedConnection, setSelectedConnection] =
        useState<UserConnection | null>(null);

    const [searchText, setSearchText] = useState("");

    const [selectedOrganization, setSelectedOrganization] = useState<{ id: string; name: string } | undefined>(undefined);
    const [selectedFarm, setSelectedFarm] = useState<{ id: string; name: string } | undefined>(undefined);

    const [farmOrField, setFarmOrField] = useState<"farm" | "field">("field");

    const objectToConnect: "organization" | "farm" | "field" = useMemo(() => {
        if (selectedConnection?.platform === Platform.JohnDeere && !selectedOrganization) {
            return "organization";
        }
        if (selectedConnection?.platform === Platform.JohnDeere && selectedOrganization && farmOrField === "farm" && !selectedFarm) {
            return "farm";
        }
        return "field";
    }, [selectedConnection, selectedOrganization, farmOrField, selectedFarm]);

    const getAccountFields = useGetRequest<{ id: string; name: string; farm: string; grower: string; linked: boolean }[]>({
        url: `user_connections/${selectedConnection?.id}/fields/`,
        params: {
            organization: selectedOrganization?.id,
            farm: selectedFarm?.id,
        },
        enabled: !!selectedConnection &&
            objectToConnect === "field",
        errorMessage: t("error.failed_to_fetch_fields"),
    });

    const getAccountFarms = useGetRequest<{ id: string; name: string }[]>({
        url: `user_connections/${selectedConnection?.id}/farms/`,
        params: {
            organization: selectedOrganization?.id,
        },
        enabled: !!selectedConnection?.id && objectToConnect === "farm",
        errorMessage: t("error.failed_to_fetch_farms"),
    });

    const getAccountOrganizations = useGetRequest<
        [{ id: string; name: string }]
    >({
        url: `user_connections/${selectedConnection?.id}/organizations/`,
        enabled: selectedConnection?.platform === Platform.JohnDeere,
        errorMessage: t("error.failed_to_fetch_organizations"),
    });

    const fieldOptions = useMemo(() => {
        if (!getAccountFields.data) return [];
        return getAccountFields.data.filter((connection) => {
            if (!searchText) return true;
            const lowerSearchText = searchText.toLowerCase();
            return (
                connection?.name
                    ?.toLowerCase()
                    ?.includes(lowerSearchText) ||
                connection?.farm
                    ?.toLowerCase()
                    ?.includes(lowerSearchText) ||
                connection?.grower
                    ?.toLowerCase()
                    ?.includes(lowerSearchText)
            );
        });
    }, [getAccountFields.data, searchText]);

    const farmOptions = useMemo(() => {
        return getAccountFarms.data?.filter((farm) => {
            if (!searchText) return true;
            const lowerSearchText = searchText.toLowerCase();
            return farm?.name?.toLowerCase()?.includes(lowerSearchText);
        });
    }, [getAccountFarms.data, searchText]);

    const organizationOptions = useMemo(() => {
        return getAccountOrganizations.data?.filter((connection) => {
            if (!searchText) return true;
            const lowerSearchText = searchText.toLowerCase();
            return connection?.name?.toLowerCase()?.includes(lowerSearchText);
        });
    }, [getAccountOrganizations.data, searchText]);

    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    useEffect(() => {
        const linkedFields = fieldOptions.filter((field) => field.linked);
        if (linkedFields.length > 0) {
            setSelectedOptions(linkedFields.map((field) => field.id));
        }
    }, [fieldOptions]);

    const selectedOptionsNotLinked = useMemo(() => {
        return selectedOptions.filter((option) => {
            const field = fieldOptions.find(
                (field) => field.id === option,
            );
            return field && !field.linked;
        });
    }, [selectedOptions, fieldOptions]);

    const createField = useMutateRequest<Field>({
        url: "fields/",
        method: "post",
        successMessage: t("success.created_field"),
        errorMessage: t("error.failed_to_create_field"),
    });

    const selectionMode: "single" | "multiple" = useMemo(() => {
        return !fieldId && objectToConnect === "field" ? "multiple" : "single";
    }, [fieldId, objectToConnect]);

    const createConnectionRequest = useMutateRequest({
        url: "field_connections/",
        method: "post",
        successMessage: t("success.created_connection"),
        errorMessage: t("error.failed_to_create_connection"),
    });

    const [syncOperations, setSyncOperations] = useState(false);

    const createConnection = useCallback(
        (fieldId: number, externalId: string, onFinish?: () => void) => {
            createConnectionRequest.mutate(
                {
                    data: {
                        field: fieldId,
                        user_connection: selectedConnection?.id,
                        platform_field_id: externalId,
                        platform_organization_id: selectedOrganization?.id,
                        sync_operations: syncOperations,
                    },
                },
                {
                    onSuccess: onFinish,
                    onError: onFinish,
                },
            );
        },
        [createConnectionRequest, selectedConnection, selectedOrganization, syncOperations],
    );

    const createFields = useCallback(() => {
        if (selectedOptionsNotLinked.length === 0) return;
        const selectedFields = fieldOptions.filter((option) => {
            return selectedOptionsNotLinked.includes(option.id);
        });
        for (let i = 0; i < selectedFields.length; i++) {
            createField.mutate(
                {
                    data: {
                        grower: growerId,
                        name: selectedFields[i]?.name,
                    },
                },
                {
                    onSuccess: (newField) => {
                        createConnection(
                            newField.id,
                            selectedFields[i]?.id,
                            () => {
                                if (i === selectedFields.length - 1) {
                                    navigate({
                                        to:
                                            selectedFields.length === 1
                                                ? `/dashboard/fields/${newField.id}/setup`
                                                : "/dashboard/fields",
                                    });
                                }
                            },
                        );
                    },
                },
            );
        }
    }, [
        selectedOptionsNotLinked,
        fieldOptions,
        createField,
        createConnection,
        growerId,
        navigate,
    ]);

    return (
        <div className="min-h-[80vh]">
            {!selectedConnection ? (
                <div className="flex flex-col gap-2 w-full">
                    <div className="flex flex-row justify-between items-center">
                        <p className="text-sm font-bold text-primary">
                            Choose an account to connect
                        </p>
                        <Button
                            color="primary"
                            as={Link}
                            to="/dashboard/account-settings/connections"
                            size="sm"
                        >
                            Manage Connections
                            <FontAwesomeIcon
                                icon={faUpRightFromSquare}
                                className="ml-2"
                            />
                        </Button>
                    </div>
                    <Input
                        value={connectionSearchText}
                        onChange={(e) =>
                            setConnectionSearchText(e.target.value)
                        }
                        placeholder="Search..."
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                    />
                    {getConnections.isLoading ? (
                        <div className="flex flex-col w-full gap-2">
                            <Skeleton className="h-12 w-full rounded-lg" />
                            <Skeleton className="h-12 w-full rounded-lg" />
                            <Skeleton className="h-12 w-full rounded-lg" />
                        </div>
                    ) : accountOptions.length === 0 ? (
                        <p className="text-center text-gray-500">
                            No connections found. Please connect your account to
                            a platform.
                        </p>
                    ) : (
                        <>
                            {accountOptions.map((connection) => {
                                const platform = connection.platform;
                                const logo = PLATFORM_LOGOS[platform];
                                return (
                                    <Card key={connection.id}>
                                        <CardBody
                                            className="hover:cursor-pointer"
                                            onClick={() => {
                                                setSelectedConnection(
                                                    connection,
                                                );
                                            }}
                                        >
                                            <div className="flex flex-col items-center">
                                                <img
                                                    src={logo}
                                                    alt={platform}
                                                    className="h-10 w-auto object-contain"
                                                    style={{
                                                        maxWidth: 250,
                                                        minWidth: 100,
                                                    }}
                                                />
                                                <p className="text-sm">
                                                    {connection.username}
                                                </p>
                                            </div>
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </>
                    )}
                </div>
            ) : (
                <div className="flex flex-col w-full justify-center items-center gap-2">
                    <Card className="w-full h-fit">
                        <CardBody>
                            <div className="flex flex-row w-full items-center justify-start">
                                <Button
                                    variant="flat"
                                    size="sm"
                                    isIconOnly
                                    onPress={() => {
                                        setSelectedConnection(null);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Button>
                                <div className="flex flex-col items-center w-full gap-1">
                                    <img
                                        src={
                                            PLATFORM_LOGOS[
                                            selectedConnection.platform
                                            ]
                                        }
                                        alt={selectedConnection.platform}
                                        className="h-12 w-auto object-contain"
                                        style={{ maxWidth: 250 }}
                                    />
                                    <p className="text-sm">
                                        {selectedConnection.username}
                                    </p>
                                    <div className="flex flex-row gap-2">
                                        {selectedOrganization && (
                                            <Chip
                                                size="sm"
                                                radius="sm"
                                                color="primary"
                                                variant="flat"
                                            >
                                                <FontAwesomeIcon icon={faXmark} onClick={() => {
                                                    setSelectedOrganization(undefined)
                                                    setSelectedFarm(undefined)
                                                }} className="mr-2 hover:cursor-pointer" />
                                                {selectedOrganization.name}
                                            </Chip>
                                        )}
                                        {selectedFarm && (
                                            <Chip
                                                size="sm"
                                                radius="sm"
                                                color="primary"
                                                variant="flat"
                                            >
                                                <FontAwesomeIcon icon={faXmark} onClick={() => setSelectedFarm(undefined)} className="mr-2 hover:cursor-pointer" />
                                                {selectedFarm.name}
                                            </Chip>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                    <div className="flex flex-col items-center justify-center w-full gap-2">
                        <div className="flex flex-row justify-between w-full gap-2">
                            <Input
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search..."
                                startContent={
                                    <FontAwesomeIcon icon={faSearch} />
                                }
                            />
                            {selectedConnection?.platform === Platform.JohnDeere && selectedOrganization && !selectedFarm && (
                                <ButtonGroup>
                                    <Button size="sm" onPress={() => setFarmOrField("farm")} color={farmOrField === "farm" ? "primary" : "default"}>
                                        Farms
                                    </Button>
                                    <Button size="sm" onPress={() => setFarmOrField("field")} color={farmOrField === "field" ? "primary" : "default"}>
                                        Fields
                                    </Button>
                                </ButtonGroup>
                            )}
                        </div>
                        <div className="flex flex-row justify-between w-full">
                            {selectedConnection?.platform === Platform.JohnDeere && (
                                <Switch size="sm" isSelected={syncOperations} onValueChange={(value) => setSyncOperations(value)}>
                                    {t("field_management.sync_operations", {
                                        ns: "setup",
                                    })}
                                </Switch>
                            )}
                            {selectionMode === "multiple" && (
                                <Checkbox
                                    className="mx-2"
                                    size="lg"
                                    onValueChange={(isSelected) => {
                                        if (isSelected) {
                                            setSelectedOptions(
                                                fieldOptions.map(
                                                    (field) => field.id,
                                                ),
                                            );
                                        } else {
                                            setSelectedOptions(
                                                fieldOptions
                                                    .filter(
                                                        (field) => field.linked,
                                                    )
                                                    .map((field) => field.id),
                                            );
                                        }
                                    }}
                                />
                            )}
                        </div>
                        {!getAccountFarms.isLoading &&
                            objectToConnect === "farm" && (
                                <div className="flex flex-col gap-2 w-full">
                                    {farmOptions?.length === 0 ? (
                                        <p className="text-center text-gray-500">
                                            Found nothing to connect.
                                        </p>
                                    ) : (
                                        <CheckboxGroup
                                            value={selectedOptions}
                                            onValueChange={setSelectedOptions}
                                            className=" w-full pb-4"
                                        >
                                            {farmOptions?.map((farm) => (
                                                <Card key={farm.id}>
                                                    <CardBody className="flex flex-row justify-start items-center w-full px-4 hover:cursor-pointer" onClick={() => setSelectedFarm(farm)}>
                                                        <p className="truncate max-w-xs">
                                                            {farm.name}
                                                        </p>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </CheckboxGroup>
                                    )}
                                </div>
                            )}
                        {!getAccountFields.isLoading &&
                            objectToConnect === "field" && (
                                <>
                                    {fieldOptions.length === 0 ? (
                                        <p className="text-center text-gray-500">
                                            Found nothing to connect.
                                        </p>
                                    ) : (
                                        <CheckboxGroup
                                            value={selectedOptions}
                                            onValueChange={setSelectedOptions}
                                            className=" w-full pb-4"
                                        >
                                            {fieldOptions.map((field) => (
                                                <Card key={field.id}>
                                                    <CardBody className="flex flex-row justify-between items-center w-full px-4">
                                                        <div className="flex flex-col">
                                                            <p className="truncate max-w-xs">
                                                                {
                                                                    field.name
                                                                }
                                                            </p>
                                                            <p className="truncate max-w-xs">
                                                                {field?.farm ??
                                                                    ""}
                                                            </p>
                                                            <p className="text-gray-500 truncate max-w-xs">
                                                                {field?.grower ??
                                                                    ""}
                                                            </p>
                                                        </div>
                                                        {selectionMode ===
                                                            "single" &&
                                                            fieldId ? (
                                                            <Button
                                                                color="secondary"
                                                                size="sm"
                                                                onPress={() => {
                                                                    createConnection(
                                                                        fieldId,
                                                                        field.id,
                                                                        () => {
                                                                            if (
                                                                                onFinish
                                                                            ) {
                                                                                onFinish(
                                                                                    ConnectionType.Field,
                                                                                    selectedConnection.id,
                                                                                );
                                                                            }
                                                                        },
                                                                    );
                                                                }}
                                                                isLoading={
                                                                    createConnectionRequest.isPending
                                                                }
                                                                isDisabled={
                                                                    field.linked
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faLink
                                                                    }
                                                                />
                                                                {field.linked
                                                                    ? "Connected"
                                                                    : rxId
                                                                        ? "Export"
                                                                        : "Connect"}
                                                            </Button>
                                                        ) : (
                                                            <Checkbox
                                                                size="lg"
                                                                disableAnimation
                                                                value={field.id}
                                                                defaultSelected={
                                                                    field.linked
                                                                }
                                                                isReadOnly={
                                                                    field.linked
                                                                }
                                                                icon={
                                                                    field.linked ? (
                                                                        <FontAwesomeIcon
                                                                            icon={
                                                                                faLink
                                                                            }
                                                                        />
                                                                    ) : undefined
                                                                }
                                                            />
                                                        )}
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </CheckboxGroup>
                                    )}
                                    {selectionMode === "multiple" && (
                                        <div className="sticky bottom-0 w-full bg-white z-10">
                                            <Button
                                                color="secondary"
                                                size="lg"
                                                fullWidth
                                                isDisabled={
                                                    !selectedOptionsNotLinked.length
                                                }
                                                isLoading={
                                                    createField.isPending ||
                                                    createConnectionRequest.isPending
                                                }
                                                onPress={createFields}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faLink}
                                                />
                                                {selectedOptionsNotLinked.length ===
                                                    0
                                                    ? "Select Fields to Connect"
                                                    : selectedOptionsNotLinked.length >
                                                        1
                                                        ? `Connect ${selectedOptionsNotLinked.length} fields`
                                                        : "Connect field"}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        {!getAccountOrganizations.isLoading &&
                            objectToConnect === "organization" && (
                                <div className="flex flex-col gap-2 w-full">
                                    {organizationOptions?.length === 0 ? (
                                        <p className="text-center text-gray-500">
                                            Found nothing to connect.
                                        </p>
                                    ) : (
                                        <>
                                            {organizationOptions?.map(
                                                (organization) => (
                                                    <Card key={organization.id}>
                                                        <CardBody className={`flex flex-row justify-between items-center w-full px-4 ${!rxId ? "cursor-pointer" : ""}`} onClick={() => {
                                                            setSelectedOrganization(organization);
                                                            setSearchText("");
                                                        }}>
                                                            <div className="flex flex-col">
                                                                <p className="truncate max-w-xs">
                                                                    {
                                                                        organization.name
                                                                    }
                                                                </p>
                                                            </div>
                                                            {rxId && (<Button
                                                                color="secondary"
                                                                size="sm"
                                                                onPress={() => {
                                                                    if (
                                                                        onFinish
                                                                    ) {
                                                                        onFinish(
                                                                            ConnectionType.User,
                                                                            selectedConnection.id,
                                                                            organization.id,
                                                                        );
                                                                    }
                                                                }}
                                                                isLoading={
                                                                    createConnectionRequest.isPending
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faLink
                                                                    }
                                                                />
                                                                Export
                                                            </Button>)}
                                                        </CardBody>
                                                    </Card>
                                                ),
                                            )}
                                        </>
                                    )}
                                    {selectionMode === "multiple" && (
                                        <div className="sticky bottom-0 w-full bg-white z-10">
                                            <Button
                                                color="secondary"
                                                size="lg"
                                                fullWidth
                                                isDisabled={
                                                    !selectedOptionsNotLinked.length
                                                }
                                                isLoading={
                                                    createField.isPending ||
                                                    createConnectionRequest.isPending
                                                }
                                                onPress={createFields}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faLink}
                                                />
                                                {selectedOptionsNotLinked.length ===
                                                    0
                                                    ? "Select Fields to Connect"
                                                    : selectedOptionsNotLinked.length >
                                                        1
                                                        ? `Connect ${selectedOptionsNotLinked.length} fields`
                                                        : "Connect field"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        {(getAccountFields.isLoading ||
                            getAccountFarms.isLoading ||
                            getAccountOrganizations.isLoading) && (
                                <div className="flex flex-col w-full gap-2">
                                    <Skeleton className="h-12 w-full rounded-lg" />
                                    <Skeleton className="h-12 w-full rounded-lg" />
                                    <Skeleton className="h-12 w-full rounded-lg" />
                                </div>
                            )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewConnection;
